import React, { useContext, useEffect, useRef } from 'react';
import { confirmInfo } from 'components/v2/confirm-dialog/confirmInfo';
import AuthService from 'services/AuthService';
import { useCompilerCompositeStatus, useGetEpdPCRInformation } from 'services/api/queries';
import { CompanyContext } from 'contexts/CompanyContextProvider';
import { CONSTRUCTION_PCR_V2_ID, RoleName } from 'constants/constants';
import { ActionState } from 'services/EpdClient';

interface Props {
  epdId: string;
  versionId: string;
}

const DeprecatedPCRVersionDialog: React.FC<Props> = ({ epdId, versionId }) => {
  const notificationStatusRef = useRef<'loading' | 'notified' | 'irrelevant'>('loading');
  const { companyId } = useContext(CompanyContext);
  const compilerCompositeStatus = useCompilerCompositeStatus(epdId).data;
  const pcrInformation = useGetEpdPCRInformation(versionId).data;

  useEffect(() => {
    if (notificationStatusRef.current === 'notified' || notificationStatusRef.current === 'irrelevant') {
      return;
    }

    if (pcrInformation === undefined || !companyId || !compilerCompositeStatus) {
      return;
    }

    const isPcrV2 = !pcrInformation.pcr || pcrInformation.pcr.id === CONSTRUCTION_PCR_V2_ID;
    const isEditable = compilerCompositeStatus.fieldsState.mainContent === ActionState.Enabled;
    const canEdit = AuthService.getCompanyRoles(companyId).some(
      (r) => r === RoleName.EPD_OWNER || r === RoleName.LCA_PRACTITIONER
    );

    const shouldNotify = !isPcrV2 && isEditable && canEdit;
    if (!shouldNotify) {
      notificationStatusRef.current = 'irrelevant';
      return;
    }

    // HACK: Without setTimeout the dialog isn't showing up with React StrictMode.
    setTimeout(() => {
      confirmInfo({
        caption: 'This EPD is currently based on PCR 2019:14 v1.3.4.',
        description:
          'To update it using PCR 2019:14 v2, please select the new version under PCR information. Be aware that the updated requirements will then apply to your EPD.',
      });
    });
    notificationStatusRef.current = 'notified';
  }, [pcrInformation, companyId, compilerCompositeStatus]);

  return null;
};

export default DeprecatedPCRVersionDialog;
